import { gql, useMutation } from '@apollo/client';
import { Box, Typography, Divider, Stack, CardOverflow, Button, FormControl, Input, FormLabel } from '@mui/joy';
import { Card, CardActions } from '@mui/joy';
import { useEffect, useReducer } from 'react';
import { useFeedback } from './feedback/Service';
import { getCdnFileUrl, parseGraphQLError } from '../utils';
import useUser from './auth/useUser';
import { CORE_TALENT_FIELDS } from '../gql/fragments/talent.fragment';
import DropZone from './DropZone';
import FileUpload from './FileUpload';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import useUploadPdf from './useUploadPdf';
import { useParams } from 'react-router';
import { OpenInNew } from '@mui/icons-material';
import { socialMediaDictionary } from '../social';

const LINKED_IN_URL = socialMediaDictionary.linkedin.baseUrl;

const CREATE_TALENT = gql`
  ${CORE_TALENT_FIELDS}
  mutation CreateTalent($talent: TalentInput!) {
    createTalent(talent: $talent) {
      ...CoreTalentFields
    }
  }
`;

let initialState = {
  linkedin: "",
  pdfUrl: "",
}

// const numericFields = [];

const reducer = (state, newState) => ({
  ...state,
  ...newState
})

const Talent = () => {
  const [state, setState] = useReducer(reducer, initialState);
  const feedback = useFeedback();
  const { user } = useUser();
  const [uploadObject, uploadPdf] = useUploadPdf();
  const { jobId, clientId } = useParams();

  const [submit, { loading }] = useMutation(CREATE_TALENT, {
    update(cache, { data: { createTalent } }) {
      cache.modify({
        id: cache.identify({ _id: user._id, __typename: "User" }),
        fields: {
          talent: () => createTalent,
        }
      });
    }
  });

  const handleSubmit = async e => {
    e.preventDefault();
    console.log("handleSubmit", !!e.nativeEvent.submitter);
    const isSkip = !e.nativeEvent.submitter;
    if (!state.pdfUrl && !isSkip) {
      feedback.snackbar({ text: "Please upload a PDF file", type: "error" });
      return;
    }
    try {
      const data = { ...state, jobId, clientId };
      // for (let field of numericFields) {
      //   if (typeof data[field] === "object") {
      //     data[field] = { from: parseFloat(data[field].from), to: parseFloat(data[field].to) };
      //     if (data[field].from > data[field].to)
      //       return;
      //   } else
      //     data[field] = parseFloat(data[field]);
      // }
      if (isSkip) {
          data.pdfUrl = data.pdfUrl || "SKIPPED";
          data.linkedin = data.linkedin || "SKIPPED";
      }
      console.log(data);
      await submit(({ variables: { talent: data } }));
      feedback.snackbar({ text: "Changes saved!", type: "success" });
      if (!user.talent) {
        window.gtag('event', 'talent_profile', {});
      }
      // if (redirect)
      // navigate(redirect);
    } catch (error) {
      console.error("ClientForm error", error);
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
    // console.log(talent);
  }

  const handleFileChange = async (file) => {
    console.log("file", file);
    const pdfUpload = await uploadPdf(file);
    setState({ pdfUrl: pdfUpload });
  }

  const handleChange = ({ target: { name, value, checked, type } }) => {
    if (name === "linkedin") {
      setState({ linkedin: value.replace(LINKED_IN_URL, "") });
      return;
    }
    setState({ [name]: type === "checkbox" ? checked : value /* (type === "number" ? parseFloat(value) : value) */ });
  }

  useEffect(() => {
    if (user?.talent) {
      setState({
        ...user.talent,
        createdAt: undefined,
        updatedAt: undefined,
        jobs: undefined,
        __typename: undefined,
        pdfStatus: undefined,
        applications: undefined,
      });
    }
  }, [user]);

  const isDemo = clientId && user?.client?._id === clientId;

  return (
    <Box
      component="main"
      sx={{
        my: 'auto',
        py: 2,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: "100%",
        maxWidth: 'sm',
        mx: 'auto',
        borderRadius: 'sm',
        '& form': {
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        },
        // [`& .MuiFormLabel-asterisk`]: {
        //   visibility: 'hidden',
        // },
      }}
    >
      <Card>
        <Box sx={{ mb: 1 }}>
          <Typography level="title-md" gutterBottom>Profile</Typography>
          <Typography level="body-sm">
            Provide your LinkedIn profile and upload your CV in PDF format.
          </Typography>
        </Box>
        <Divider />
        <Stack spacing={2} sx={{ my: 1 }}>
          <form id='talent-form' onSubmit={handleSubmit}>
            <FormControl required>
              <FormLabel>LinkedIn</FormLabel>
              <Input
                size="sm"
                placeholder="URL"
                value={state.linkedin}
                onChange={handleChange}
                name="linkedin"
                startDecorator={LINKED_IN_URL}
              />
            </FormControl>
            {/* {user?.talent?.pdfContent &&
            <FormControl>
              <FormLabel>Parsed CV</FormLabel>
              <Textarea
                size="sm"
                value={user?.talent.pdfContent}
                onChange={handleChange}
                name="pdfContent"
                readOnly
                maxRows={7}
              />
            </FormControl>
          } */}
          </form>
          {isDemo && <Typography level="body-sm" color="neutral">Since you are in demo mode, you can click Skip without uploading a CV. However, we recommend uploading a CV to experience the full functionality of the platform. If you are using ATS integration, candidate&apos;s CV will be automatically parsed and linked, so a candidate will proceed to the interview seamlessly.</Typography>}
          <FormLabel component="p">Upload CV</FormLabel>
          <DropZone onFileSelect={handleFileChange} title="with your CV" fileExists={state.pdfUrl} />
          {uploadObject &&
            <FileUpload
              icon={<InsertDriveFileRoundedIcon />}
              fileName={uploadObject.name}
              fileSize={uploadObject.size ? `${Math.round(uploadObject.size / 1024)} KB` : "0 KB"}
              progress={uploadObject.progress}
              onAbort={uploadObject.abort}
            />
          }
        </Stack>
        <CardOverflow sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <CardActions sx={{ alignSelf: 'flex-end', pt: 2 }}>
            {/* <Button size="sm" variant="outlined" color="neutral">
              Cancel
            </Button> */}
            {state.pdfUrl &&
              <Button
                variant="soft"
                component="a"
                href={getCdnFileUrl(state.pdfUrl)}
                target="_blank"
                rel="noopener noreferrer"
                endDecorator={<OpenInNew />}
                color="neutral"
              >
                View PDF
              </Button>
            }
            {isDemo &&
              <Button size="sm" variant="plain" color="primary" onClick={handleSubmit} loading={loading}>
                Skip
              </Button>
            }
            <Button size="sm" variant="solid" color="primary" type='submit' form='talent-form' loading={loading}>
              Save
            </Button>
          </CardActions>
        </CardOverflow>
      </Card>
    </Box>
  );
}

export default Talent;